import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Card,
  Typography,
  Space,
  Row,
  Col
} from 'antd';
import {
  CloseOutlined
} from '@ant-design/icons';
import { Lead } from '../services/api';
import apiService from '../services/api';
import { extractLeadFields, hasMeaningfulValue } from '../utils/fieldMapping';

const { Title, Text } = Typography;

interface ProsegurFormProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  lead: Lead | null;
}

interface ProsegurFormData {
  nome?: string;
  nif?: string;
  moradaInstalacao?: string;
  email?: string;
  telemovel?: string;
  iban?: string;
}

const ProsegurForm: React.FC<ProsegurFormProps> = ({ open, onClose, onSave, lead }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ProsegurFormData>({});

  // Load saved data when modal opens
  useEffect(() => {
    const loadSavedData = async () => {
      if (open && lead?.id) {
        try {
          const firstStored = (fv: Record<string, any>, keys: string[]): string | undefined => {
            for (const k of keys) {
              const v = fv[k];
              if (hasMeaningfulValue(v)) return String(v);
            }
            return undefined;
          };

          const response = await apiService.getFieldValues(lead.id);
          if (response.success && response.field_values) {
            const fv = response.field_values;
            const leadFields = extractLeadFields(lead);
            const savedData: ProsegurFormData = {
              nome:
                fv.prosegur_nome ||
                firstStored(fv, ['Nome', 'Nome Completo', 'Nome do Cliente', 'nome', 'nomeRepresentante']) ||
                leadFields?.name ||
                '',
              nif:
                fv.prosegur_nif ||
                firstStored(fv, ['NIF', 'nif', 'Contribuinte', 'contribuinte', 'Num_Doc_Identificação_1']) ||
                leadFields?.nif ||
                '',
              moradaInstalacao:
                fv.prosegur_morada_instalacao ||
                firstStored(fv, ['Morada', 'Morada Completa', 'moradaInstalacao', 'morada_instalacao', 'Endereço']) ||
                leadFields?.address ||
                '',
              email:
                fv.prosegur_email ||
                firstStored(fv, ['Email', 'email', 'emailRepresentante', 'admin_conta_email']) ||
                leadFields?.email ||
                '',
              telemovel:
                fv.prosegur_telemovel ||
                firstStored(fv, [
                  'Telefone Contacto',
                  'Telemóvel de Contacto',
                  'Contacto',
                  'Telefone',
                  'Telemóvel',
                  'telefone_contacto',
                ]) ||
                leadFields?.phone ||
                '',
              iban: fv.prosegur_iban || '',
            };
            setFormData(savedData);
            form.setFieldsValue(savedData);
          } else {
            // No saved data, use lead data as defaults
            const leadFields = extractLeadFields(lead);
            const defaultData: ProsegurFormData = {
              nome: leadFields?.name || '',
              nif: leadFields?.nif || '',
              moradaInstalacao: leadFields?.address || '',
              email: leadFields?.email || '',
              telemovel: leadFields?.phone || '',
              iban: ''
            };
            setFormData(defaultData);
            form.setFieldsValue(defaultData);
          }
        } catch (error) {
          console.error('Error loading saved Prosegur data:', error);
          // Use lead data as defaults on error
          const leadFields = extractLeadFields(lead);
          const defaultData: ProsegurFormData = {
            nome: leadFields?.name || '',
            nif: leadFields?.nif || '',
            moradaInstalacao: leadFields?.address || '',
            email: leadFields?.email || '',
            telemovel: leadFields?.phone || '',
            iban: ''
          };
          setFormData(defaultData);
          form.setFieldsValue(defaultData);
        }
      } else if (open) {
        // No lead, reset form
        setFormData({});
        form.resetFields();
      }
    };

    loadSavedData();
  }, [open, lead, form]);

  const handleFieldChange = (field: keyof ProsegurFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNifInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 9);
    handleFieldChange('nif', value);
    form.setFieldsValue({ nif: value });
  };

  const handleNifPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 9);
    handleFieldChange('nif', pasted);
    form.setFieldsValue({ nif: pasted });
  };

  const handleTelemovelInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 9);
    handleFieldChange('telemovel', value);
    form.setFieldsValue({ telemovel: value });
  };

  const handleTelemovelPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 9);
    handleFieldChange('telemovel', pasted);
    form.setFieldsValue({ telemovel: pasted });
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      setLoading(true);

      // Prepare field values with prosegur prefix to avoid conflicts
      const fieldValues: Record<string, any> = {
        prosegur_nome: formData.nome || '',
        prosegur_nif: formData.nif || '',
        prosegur_morada_instalacao: formData.moradaInstalacao || '',
        prosegur_email: formData.email || '',
        prosegur_telemovel: formData.telemovel || '',
        prosegur_iban: formData.iban || ''
      };

      if (lead?.id) {
        await apiService.saveFieldValues(lead.id, fieldValues);
        message.success('Dados Prosegur guardados com sucesso!');
        if (onSave) {
          onSave();
        }
        onClose();
      } else {
        message.error('Lead não encontrado');
      }
    } catch (error) {
      console.error('Error saving Prosegur data:', error);
      message.error('Erro ao guardar dados');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={700}
      footer={null}
      closeIcon={
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.06)';
          }}
        >
          <CloseOutlined style={{ fontSize: '14px' }} />
        </div>
      }
      bodyStyle={{
        padding: '0',
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto'
      }}
      style={{
        borderRadius: '8px'
      }}
    >
      <div style={{
        padding: '24px 32px',
        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        marginBottom: '24px'
      }}>
        <Title level={3} style={{ margin: 0, color: 'white', fontSize: '28px', fontWeight: 700 }}>
          Formulário Prosegur
        </Title>
        <Text style={{ display: 'block', marginTop: 8, color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
          Preencha os dados do cliente
        </Text>
      </div>

      <div style={{ padding: '0 32px 24px 32px' }}>
        <Form form={form} layout="vertical">
          <Card
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e8e8e8'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Row gutter={[16, 20]}>
                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Nome <span style={{ color: '#ff4d4f' }}>*</span></span>}
                    name="nome"
                    rules={[{ required: true, message: 'Campo obrigatório' }]}
                  >
                    <Input
                      size="large"
                      placeholder="Nome completo"
                      value={formData.nome}
                      onChange={(e) => handleFieldChange('nome', e.target.value)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '16px 20px',
                        fontSize: '16px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontSize: '14px', fontWeight: 500 }}>NIF <span style={{ color: '#ff4d4f' }}>*</span></span>}
                    name="nif"
                    rules={[
                      { required: true, message: 'Campo obrigatório' },
                      { len: 9, message: 'NIF deve ter 9 dígitos' }
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="9 dígitos"
                      maxLength={9}
                      value={formData.nif}
                      onChange={handleNifInput}
                      onPaste={handleNifPaste}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '16px 20px',
                        fontSize: '16px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Morada de instalação <span style={{ color: '#ff4d4f' }}>*</span></span>}
                    name="moradaInstalacao"
                    rules={[{ required: true, message: 'Campo obrigatório' }]}
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder="Morada completa de instalação"
                      value={formData.moradaInstalacao}
                      onChange={(e) => handleFieldChange('moradaInstalacao', e.target.value)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '16px 20px',
                        fontSize: '16px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontSize: '14px', fontWeight: 500 }}>E-mail</span>}
                    name="email"
                    rules={[
                      { type: 'email', message: 'E-mail inválido' }
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="exemplo@email.com"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '16px 20px',
                        fontSize: '16px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontSize: '14px', fontWeight: 500 }}>Telemóvel <span style={{ color: '#ff4d4f' }}>*</span></span>}
                    name="telemovel"
                    rules={[
                      { required: true, message: 'Campo obrigatório' },
                      { len: 9, message: 'Telemóvel deve ter 9 dígitos' }
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="9 dígitos"
                      maxLength={9}
                      value={formData.telemovel}
                      onChange={handleTelemovelInput}
                      onPaste={handleTelemovelPaste}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '16px 20px',
                        fontSize: '16px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label={<span style={{ fontSize: '14px', fontWeight: 500 }}>IBAN</span>}
                    name="iban"
                  >
                    <Input
                      size="large"
                      placeholder="PT50 0000 0000 0000 0000 0000 0"
                      value={formData.iban}
                      onChange={(e) => handleFieldChange('iban', e.target.value.toUpperCase())}
                      style={{
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        padding: '16px 20px',
                        fontSize: '16px',
                        textTransform: 'uppercase'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#1890ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(24, 144, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Space>
          </Card>

          <div style={{ marginTop: 32, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={onClose}
              size="large"
              style={{
                minWidth: '140px',
                padding: '14px 32px',
                height: 'auto',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                background: 'white',
                border: '2px solid #e5e7eb',
                color: '#333333',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              size="large"
              style={{
                minWidth: '140px',
                padding: '14px 32px',
                height: 'auto',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(24, 144, 255, 0.4)';
                e.currentTarget.style.background = 'linear-gradient(135deg, #096dd9 0%, #0050b3 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.3)';
                e.currentTarget.style.background = 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)';
              }}
            >
              ✓ Guardar
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default ProsegurForm;

